import React from 'react';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useApolloClient } from '@apollo/client';
import { LoaderSpinner } from '../../components/loader-spinner';
import {
  DEFAULT_OFFERS_REDIRECT_PAGE,
  generateOffersPageUrl,
  getOffersByModel,
  offerViewMoreOffersStyle,
} from './helpers';
import { Dealer, ModelConfig } from '../../typing/model';
import { Offer } from '../../typing/offer';
import TrimModuleOfferCard from '../../components/trim-module-offer-card';
import { useTranslations } from '../../hooks/use-translations';
import { StyledTrimModuleOffersContainer } from './styles';
import {
  DisableFocusWrapper,
  LinkButton,
  TextCustom,
} from '@ngw6/us-components';
import { useFeatureServices } from '../../hooks/use-feature-services';
import ModalDisplay from '../../components/modal-display';
import { modalsData } from '../../store/local-storage';
import { ModalDisplayMode } from '../../typing/modal-display';
import TrimModuleNoOffers from '../../components/trim-module-no-offers';
import OffersBox from '../../components/offers-box';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import {
  onHandlePromotionButtonTracking,
  onHandlerBasicVehicleLink,
  onHandlerDataLoaded,
} from '../../tagging/tagging-helpers';

interface TrimModuleState {
  appStatus: 'loading' | 'error' | 'success' | 'initial' | 'empty';
  error?: boolean;
  dealer?: Dealer;
  offers?: Offer[];
  dealers?: Dealer[];
  modelConfig?: ModelConfig;
}
const TrimModule = () => {
  const [trimModuleAppState, setTrimModuleAppState] =
    React.useState<TrimModuleState>({ appStatus: 'initial' });
  const {
    trimModuleConfig,
    offersRedirectPageConfig = DEFAULT_OFFERS_REDIRECT_PAGE,
  } = useFeatureAppConfig();
  const { trimModuleMode } = useTranslations();
  const client = useApolloClient();
  const navigateService = useFeatureServices()['navigation-service'];
  const trackingManager = useTrackingManager();

  /**
   * handles view details modal
   * @param offer
   */
  const onHandleViewDetails = (offer: Offer) => {
    modalsData({
      offer,
      dealer: trimModuleAppState.dealer,
      active: true,
      mode: ModalDisplayMode.TRIM_MODULE_DETAILS,
      model: {
        ...trimModuleAppState.modelConfig,
        carImage:
          trimModuleConfig?.carImage ||
          trimModuleAppState.modelConfig?.carImage ||
          '',
        carImageAlt:
          trimModuleConfig?.carAlt ||
          trimModuleAppState.modelConfig?.carImageAlt ||
          '',
      },
    });

    trackingManager &&
      onHandlePromotionButtonTracking(
        trimModuleMode.offerButtonLabel,
        undefined,
        { ...trimModuleAppState, offer },
        trackingManager,
      );
  };
  /**
   * Handle redirect to more offers
   */
  const onHandleMoreOffers = () => {
    const tier1Redirect = generateOffersPageUrl(
      offersRedirectPageConfig,
      trimModuleConfig?.carlineName || '',
      trimModuleConfig?.dealer?.postalcode || '',
      navigateService,
    );
    trackingManager &&
      onHandlerBasicVehicleLink(
        tier1Redirect?.createHref(),
        trimModuleMode.viewMoreOffersCta,
        trackingManager,
        trimModuleAppState,
      );
    tier1Redirect?.push();
  };

  React.useEffect(() => {
    setTrimModuleAppState({ appStatus: 'loading' });
    getOffersByModel(client, trimModuleConfig)
      .then((offers) => {
        setTrimModuleAppState({
          appStatus: offers.offers.length ? 'success' : 'empty',
          ...offers,
        });
        trackingManager && onHandlerDataLoaded(trackingManager, offers);
      })
      .catch((error) => {
        console.log(error);
        setTrimModuleAppState({ appStatus: 'error' });
      });
    return () => {};
  }, []);

  switch (trimModuleAppState.appStatus) {
    case 'loading':
      return <LoaderSpinner label="Loading Special Offers" />;
    case 'error':
      return <></>;
    case 'empty':
      return <></>;
    case 'success':
      return (
        <OffersBox title={trimModuleMode.offerButtonLabel}>
          <StyledTrimModuleOffersContainer className="trim-module_ offers-container">
            {trimModuleAppState.offers?.length ? (
              trimModuleAppState.offers?.map((offer) => (
                <TrimModuleOfferCard
                  offer={offer}
                  onViewDetails={onHandleViewDetails}
                  key={offer.dealId}
                  translation={trimModuleMode.offerCard}
                />
              ))
            ) : (
              <TrimModuleNoOffers message={trimModuleMode.noOffersAvailable} />
            )}
            <div className="trim-module_view-more-offers">
              <DisableFocusWrapper>
                <LinkButton
                  isInline
                  onClick={onHandleMoreOffers}
                  target="_blank"
                >
                  <TextCustom {...offerViewMoreOffersStyle}>
                    {trimModuleMode.viewMoreOffersCta}
                  </TextCustom>
                </LinkButton>
              </DisableFocusWrapper>
            </div>
          </StyledTrimModuleOffersContainer>
          <ModalDisplay />
        </OffersBox>
      );
    default:
      return <></>;
  }
};

export default TrimModule;
